import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, BookOpen, TrendingUp, TrendingDown, Percent, Wallet } from "lucide-react";
import { listJournal, addJournal, editJournal, deleteJournal } from "../services/journal";
import Modal from "../components/common/Modal";
import { EmptyState, Badge } from "../components/common/Card";
import Reveal from "../components/common/Reveal";

const FILTERS = ["All", "Wins", "Losses", "Today", "This Week", "This Month"];
const empty = { pair: "", date: new Date().toISOString().slice(0, 10), time: "", direction: "UP", entry_amount: "", result: "WIN", profit_loss: "", setup_notes: "", lesson: "" };

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("All");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => setEntries(await listJournal());
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty); setEditingId(null); setModal(true); };
  const openEdit = (e) => {
    setForm({ ...empty, ...e, entry_amount: e.entry_amount ?? "", profit_loss: e.profit_loss ?? "" });
    setEditingId(e.id); setModal(true);
  };

  const save = async (ev) => {
    ev.preventDefault();
    if (!form.pair) return toast.error("Enter a pair");
    const payload = {
      ...form,
      entry_amount: parseFloat(form.entry_amount) || 0,
      profit_loss: parseFloat(form.profit_loss) || 0,
    };
    try {
      if (editingId) { await editJournal(editingId, payload); toast.success("Trade updated"); }
      else { await addJournal(payload); toast.success("Trade saved"); }
      setModal(false); load();
    } catch { toast.error("Could not save trade"); }
  };

  const remove = async (id) => { await deleteJournal(id); toast.success("Trade deleted"); load(); };

  const filtered = useMemo(() => {
    const now = new Date();
    return entries.filter((e) => {
      if (filter === "Wins") return e.result === "WIN";
      if (filter === "Losses") return e.result === "LOSS";
      const d = new Date(e.created_at || e.date);
      if (filter === "Today") return d.toDateString() === now.toDateString();
      if (filter === "This Week") return (now - d) / 86400000 <= 7;
      if (filter === "This Month") return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      return true;
    });
  }, [entries, filter]);

  const stats = useMemo(() => {
    const total = entries.length;
    const wins = entries.filter((e) => e.result === "WIN").length;
    const losses = entries.filter((e) => e.result === "LOSS").length;
    const net = entries.reduce((s, e) => s + (Number(e.profit_loss) || 0), 0);
    return { total, wins, losses, winRate: total ? Math.round((wins / total) * 100) : 0, net };
  }, [entries]);

  const statCards = [
    { label: "Total Trades", value: stats.total, icon: BookOpen },
    { label: "Win Rate", value: `${stats.winRate}%`, icon: Percent },
    { label: "Wins / Losses", value: `${stats.wins} / ${stats.losses}`, icon: TrendingUp },
    { label: "Net Result", value: `${stats.net >= 0 ? "+" : ""}$${stats.net.toFixed(2)}`, icon: Wallet, tone: stats.net >= 0 ? "up" : "down" },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500">Performance Log</div>
            <h1 className="font-head text-2xl font-bold uppercase tracking-tight text-white md:text-3xl">Trading Journal</h1>
          </div>
          <button onClick={openAdd} className="btn-silver sweep flex items-center gap-2 rounded-xl px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="journal-add-btn">
            <Plus size={15} /> Add Trade
          </button>
        </div>
      </Reveal>

      {/* stats */}
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.05}>
            <div className="surface rounded-2xl p-5">
              <s.icon size={16} className="mb-3 text-neutral-500" />
              <div className={`font-head text-xl font-bold tabular-nums md:text-2xl ${s.tone === "up" ? "text-[#8fd6b4]" : s.tone === "down" ? "text-[#e0949c]" : "text-white"}`}>{s.value}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* filters */}
      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${filter === f ? "border-white/25 bg-white/[0.06] text-white" : "border-white/10 text-neutral-500 hover:text-neutral-300"}`}
            data-testid={`journal-filter-${f.toLowerCase().replace(/\s/g, "-")}`}>
            {f}
          </button>
        ))}
      </div>

      {/* list */}
      <div className="mt-5">
        {filtered.length === 0 ? (
          <EmptyState icon={BookOpen} title="No trades yet" desc="Add your first trade to start building your performance log." />
        ) : (
          <div className="surface overflow-hidden rounded-2xl">
            <div className="hidden grid-cols-12 border-b border-white/8 px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500 md:grid">
              <div className="col-span-2">Pair</div><div className="col-span-2">Date</div>
              <div className="col-span-1">Dir</div><div className="col-span-2">Result</div>
              <div className="col-span-2 text-right">P/L</div><div className="col-span-3 text-right">Actions</div>
            </div>
            {filtered.map((e) => (
              <div key={e.id} className="grid grid-cols-2 items-center gap-y-2 border-b border-white/5 px-5 py-4 md:grid-cols-12" data-testid="journal-entry">
                <div className="col-span-1 font-mono text-sm font-semibold text-white md:col-span-2">{e.pair}</div>
                <div className="col-span-1 font-mono text-xs text-neutral-400 md:col-span-2">{e.date} {e.time}</div>
                <div className="col-span-1 md:col-span-1"><span className={e.direction === "UP" ? "text-[#8fd6b4]" : "text-[#e0949c]"}>{e.direction}</span></div>
                <div className="col-span-1 md:col-span-2">
                  <Badge tone={e.result === "WIN" ? "verified" : e.result === "LOSS" ? "rejected" : "demo"}>{e.result}</Badge>
                </div>
                <div className={`col-span-1 text-right font-mono text-sm tabular-nums md:col-span-2 ${Number(e.profit_loss) >= 0 ? "text-[#8fd6b4]" : "text-[#e0949c]"}`}>
                  {Number(e.profit_loss) >= 0 ? "+" : ""}${Number(e.profit_loss || 0).toFixed(2)}
                </div>
                <div className="col-span-1 flex justify-end gap-2 md:col-span-3">
                  <button onClick={() => openEdit(e)} className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-neutral-400 hover:text-white" data-testid="journal-edit-btn"><Pencil size={13} /></button>
                  <button onClick={() => remove(e.id)} className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-neutral-400 hover:text-rose-300" data-testid="journal-delete-btn"><Trash2 size={13} /></button>
                </div>
                {(e.setup_notes || e.lesson) && (
                  <div className="col-span-2 mt-1 text-xs text-neutral-500 md:col-span-12">
                    {e.setup_notes && <span>Setup: {e.setup_notes}. </span>}
                    {e.lesson && <span className="text-neutral-400">Lesson: {e.lesson}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* add/edit modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editingId ? "Edit trade" : "Add trade"} testid="journal-modal"
        footer={
          <>
            <button onClick={() => setModal(false)} className="btn-ghost rounded-xl px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest">Cancel</button>
            <button onClick={save} className="btn-silver rounded-xl px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="journal-save-btn">Save Trade</button>
          </>
        }>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <In label="Pair" value={form.pair} onChange={(v) => setForm({ ...form, pair: v })} testid="jf-pair" ph="EUR/USD" />
          <Sel label="Direction" value={form.direction} onChange={(v) => setForm({ ...form, direction: v })} opts={["UP", "DOWN"]} testid="jf-direction" />
          <In label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="jf-date" />
          <In label="Time" type="time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} testid="jf-time" />
          <In label="Entry amount ($)" type="number" value={form.entry_amount} onChange={(v) => setForm({ ...form, entry_amount: v })} testid="jf-entry" ph="10" />
          <Sel label="Result" value={form.result} onChange={(v) => setForm({ ...form, result: v })} opts={["WIN", "LOSS", "BREAKEVEN"]} testid="jf-result" />
          <In label="Profit / Loss ($)" type="number" value={form.profit_loss} onChange={(v) => setForm({ ...form, profit_loss: v })} testid="jf-pl" ph="8.5 or -10" full />
          <In label="Setup notes" value={form.setup_notes} onChange={(v) => setForm({ ...form, setup_notes: v })} testid="jf-notes" ph="What was the setup?" full />
          <In label="Lesson learned" value={form.lesson} onChange={(v) => setForm({ ...form, lesson: v })} testid="jf-lesson" ph="What did you learn?" full />
        </form>
      </Modal>
    </div>
  );
}

function In({ label, value, onChange, type = "text", testid, ph, full }) {
  return (
    <label className={full ? "col-span-2 block" : "block"}>
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={ph} data-testid={testid}
        className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-[#d4d8dc]/60" />
    </label>
  );
}
function Sel({ label, value, onChange, opts, testid }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-lg border border-white/10 bg-[#0c0c0e] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-[#d4d8dc]/60">
        {opts.map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}
